import React, { useState } from "react";
import JSZip from "jszip";
import { IVISAxios } from "../../Modules/Axios/IVISAxios"; // 경로 확인 필수
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Modal,
  Box,
  LinearProgress,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import moment from "moment";
import { GridCloseIcon } from "@mui/x-data-grid";

const fileTypeMap = {
  신청서: ["ar", "arb", "ac", "aa"],
  개별서류: [],
  통합파일: ["0"],
};

const DownloadSelectedZipButton = ({ selectedRows }) => {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState(Object.keys(fileTypeMap));
  const [downloadProgress, setDownloadProgress] = useState({});
  const [completed, setCompleted] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    if (loading) return;
    setOpen(false);
    setCompleted(false);
    setDownloadProgress({});
  };

  const handleTypeChange = (event) => {
    const { name, checked } = event.target;
    setSelectedTypes((prev) =>
      checked ? [...prev, name] : prev.filter((type) => type !== name)
    );
  };

  const handleDownload = async () => {
    if (
      !selectedRows ||
      selectedRows.length === 0 ||
      selectedTypes.length === 0
    )
      return;

    setLoading(true);
    const zip = new JSZip();
    const totalFiles = selectedRows.reduce(
      (acc, row) => acc + row.attacheds.length,
      0
    );
    let downloadedFiles = 0;

    for (const row of selectedRows) {
      const { vin, attacheds } = row;
      const vinFolder = zip.folder(vin);

      const filteredFiles = attacheds.filter((file) => {
        const { type } = file;
        return selectedTypes.some((selectedType) => {
          if (selectedType === "개별서류") {
            return !isNaN(type) && type !== "0";
          }
          return fileTypeMap[selectedType].includes(type);
        });
      });

      for (const file of filteredFiles) {
        const { type, fileName } = file;
        try {
          const res = await IVISAxios.get("/attached", {
            params: { vin, type },
            responseType: "blob",
          });
          vinFolder.file(fileName || `${type}.pdf`, res.data);
          downloadedFiles++;
          setDownloadProgress((prev) => ({
            ...prev,
            [`${vin}-${fileName}`]: true,
          }));
        } catch (err) {
          console.error(`${vin} - ${fileName} 다운로드 실패`, err);
          setDownloadProgress((prev) => ({
            ...prev,
            [`${vin}-${fileName}`]: false,
          }));
        }
      }

      setDownloadProgress((prev) => ({
        ...prev,
        [`VIN_DONE_${vin}`]: true,
      }));
    }

    try {
      const blob = await zip.generateAsync({ type: "blob" }, (metadata) => {
        const progress = Math.floor(
          (metadata.percent * downloadedFiles) / totalFiles
        );
        setDownloadProgress((prev) => ({
          ...prev,
          overall: progress,
        }));
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `일괄-${moment().format("YYMMDD-HH:mm:ss")}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setCompleted(true);
    } catch (e) {
      console.error("ZIP 생성 실패", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleOpen}
        disabled={selectedRows.length === 0}
      >
        선택한 차대번호 첨부파일 일괄 다운로드
      </Button>
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            px: 2.5,
            py: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            borderRadius: 2,
          }}
        >
          <Box>
            <Typography variant="h6">첨부파일 zip 일괄 다운로드</Typography>
          </Box>
          <Box>
            <IconButton
              onClick={handleClose}
              sx={{ position: "absolute", top: "10px", right: "10px" }}
            >
              <GridCloseIcon />
            </IconButton>
            <Typography sx={{ pb: 1, borderBottom: "1px solid grey" }}>
              선택한 차대번호 {selectedRows.length}건
            </Typography>
            <List sx={{ maxHeight: "300px", overflowY: "auto" }}>
              {selectedRows.map((row) => {
                const isDone = downloadProgress[`VIN_DONE_${row.vin}`];
                return (
                  <ListItem key={row.vin} sx={{ py: 0, px: 1 }}>
                    <ListItemText primary={row.vin} />
                    {isDone && (
                      <CheckCircleIcon
                        fontSize="small"
                        sx={{ color: "green" }}
                      />
                    )}
                  </ListItem>
                );
              })}
            </List>
          </Box>
          <Box>
            <FormGroup
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 3,
              }}
            >
              의 모든
              <Box>
                {Object.keys(fileTypeMap).map((type) => (
                  <FormControlLabel
                    key={type}
                    control={
                      <Checkbox
                        checked={selectedTypes.includes(type)}
                        onChange={handleTypeChange}
                        name={type}
                        size="small"
                      />
                    }
                    label={type}
                  />
                ))}
              </Box>
            </FormGroup>
            {loading && downloadProgress.overall === 0 && (
              <>개별 파일을 다운로드하는 중입니다...</>
            )}
            {loading && downloadProgress.overall > 0 && (
              <>
                <LinearProgress
                  variant="determinate"
                  value={downloadProgress.overall || 0}
                  sx={{ my: 2 }}
                />
                <Typography variant="body2">
                  압축하는 중... {downloadProgress.overall || 0}%
                </Typography>
              </>
            )}
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              onClick={handleDownload}
              variant="contained"
              disabled={loading || selectedTypes.length === 0 || completed}
            >
              {completed ? "완료" : "일괄 다운로드"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default DownloadSelectedZipButton;
